import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { api } from "../../utils/api";
import { useApp } from "../../context/AppContext";
import { t } from "../../utils/i18n";
import { MOVE_MODE_ICONS } from "./MoveIcons";
import { initials, MOVE_MODE_I18N_KEY } from "./MoveCard";

// Calqué sur RequestDetailModal (components/PowerRequestModals.tsx) : même chrome de modale
// (portail direct sur document.body, overlay centré) — voir son propre commentaire pour le
// pourquoi du portail (Safari iOS + position:fixed dans un ancêtre WebkitOverflowScrolling).
export default function MoveDetailModal({ moveId, onClose, onChanged }: { moveId: string; onClose: () => void; onChanged?: () => void }) {
  const { user, lang } = useApp();
  const [move, setMove] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/publications/${moveId}`).then(data => setMove(data?.publication || null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [moveId]);

  const isOwner = move?.user_id === user?.id;
  const isConfirmed = isOwner || move?.my_participation_status === "confirmed";
  const myEntry = (move?.confirmed_participants || []).find((p: any) => p.id === user?.id);
  const onMyWay = !!myEntry?.on_my_way;

  const requestSeat = () => {
    if (!move || busy || move.my_participation_status) return;
    setBusy(true);
    setError("");
    const success_url = `${window.location.origin}/move?payment=success`;
    const cancel_url = `${window.location.origin}/move?payment=cancelled`;
    api.post(`/publications/${move.id}/participate`, { success_url, cancel_url }).then(data => {
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data?.status) {
        setMove((prev: any) => prev ? { ...prev, my_participation_status: data.status } : prev);
        onChanged?.();
      } else if (data?.error) {
        setError(data.error);
      }
    }).catch(() => setError("Erreur de connexion")).finally(() => setBusy(false));
  };

  const toggleOnMyWay = () => {
    if (!move || busy) return;
    setBusy(true);
    setError("");
    api.post(`/publications/${move.id}/on-my-way`, {}).then(data => {
      if (data?.error) { setError(data.error); return; }
      load();
      onChanged?.();
    }).catch(() => setError("Erreur de connexion")).finally(() => setBusy(false));
  };

  if (loading || !move) {
    return ReactDOM.createPortal(
      <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ backgroundColor: "white", borderRadius: 20, padding: 40, color: "#999" }}>{t(lang, "msg_loading")}</div>
      </div>,
      document.body
    );
  }

  const Icon = MOVE_MODE_ICONS[move.move_mode as keyof typeof MOVE_MODE_ICONS] || MOVE_MODE_ICONS.ride;
  const showVehicle = move.move_mode === "ride" && move.vehicle_make;
  const full = move.current_places >= move.max_places;

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 24, width: "100%", maxWidth: 440, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={18} color="#0a0a0a" />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{t(lang, MOVE_MODE_I18N_KEY[move.move_mode as keyof typeof MOVE_MODE_ICONS])}</span>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>

        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>{move.title}</div>
        <div style={{ fontSize: 14, color: "#0a0a0a", fontWeight: 600, marginBottom: 2 }}>{move.location_name} → {move.destination_name}</div>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          {move.event_date && new Date(move.event_date).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
        </div>
        {showVehicle && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#666", marginBottom: 12 }}>
            <Icon size={14} color="#666" /> {[move.vehicle_make, move.vehicle_model].filter(Boolean).join(" ")}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, overflow: "hidden" }}>
            {move.author_avatar ? <img src={move.author_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(move.author_name)}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{move.author_name}</span>
        </div>

        {(move.confirmed_participants || []).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#999", marginBottom: 8, textTransform: "uppercase" }}>{t(lang, "move_travelers_label")}</div>
            {move.confirmed_participants.map((p: any) => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "#333", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 700, overflow: "hidden" }}>
                  {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(p.name)}
                </div>
                <span style={{ fontSize: 13 }}>{p.name}</span>
                {p.on_my_way && <span style={{ fontSize: 11, fontWeight: 700, color: "#2ecc71", backgroundColor: "#2ecc7122", borderRadius: 10, padding: "2px 8px" }}>{t(lang, "move_on_my_way_badge")}</span>}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{move.is_free ? (t(lang, "free") || "Gratuit") : `${move.price}€/place`}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: full ? "#c0392b" : "#666" }}>{full ? t(lang, "move_full") : `${move.current_places}/${move.max_places} ${t(lang, "move_seats_suffix")}`}</span>
        </div>

        {error && <div style={{ backgroundColor: "#F5F5F5", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#c0392b", marginBottom: 12 }}>{error}</div>}

        {!isOwner && !move.my_participation_status && (
          <button onClick={requestSeat} disabled={busy || full} style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", backgroundColor: full ? "#E0E0E0" : "#0a0a0a", color: full ? "#999" : "white", fontSize: 14, fontWeight: 700, cursor: full ? "not-allowed" : "pointer", marginBottom: 10 }}>
            {full ? t(lang, "move_full") : busy ? "..." : t(lang, "move_request_seat")}
          </button>
        )}
        {!isOwner && move.my_participation_status === "pending" && (
          <div style={{ textAlign: "center", fontSize: 13, color: "#999", marginBottom: 10 }}>{t(lang, "move_pending_request")}</div>
        )}

        {isConfirmed && (
          <button onClick={toggleOnMyWay} disabled={busy} style={{ width: "100%", padding: 14, borderRadius: 14, border: onMyWay ? "1.5px solid #E0E0E0" : "none", backgroundColor: onMyWay ? "white" : "#2ecc71", color: onMyWay ? "#0a0a0a" : "white", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            {busy ? "..." : onMyWay ? t(lang, "move_on_my_way_off") : t(lang, "move_on_my_way_on")}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
